import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { HelperService } from 'src/app/core/services/helper.service';
import { filter } from 'rxjs/operators';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import {
  IDLE_SIGNED_OUT_MESSAGE,
  IDLE_SIGNED_OUT_STORAGE_KEY,
} from 'src/app/core/services/idle-timeout.service';
import { MsalRedirectComponent } from 'src/app/msal-redirect/msal-redirect.component';

@Component({
  selector: 'app-side-login',
  imports: [RouterModule, MaterialModule, FormsModule, ReactiveFormsModule, MsalRedirectComponent],
  templateUrl: './side-login.component.html',
  //styleUrls: ['./side-login.component.scss'],
  //encapsulation: ViewEncapsulation.None,
})
export class AppSideLoginComponent {
  env = environment.envName;

  public showMsalRedirect = false;

  form = new FormGroup({
    uname: new FormControl('', [Validators.required, Validators.minLength(6)]),
    password: new FormControl('', [Validators.required]),
  });

  private readonly _destroy$ = new Subject<void>();
  private interactionInProgress = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public helperService: HelperService,
    private msalService: MsalService,
    private router: Router,
    private route: ActivatedRoute,
    private msalBroadcastService: MsalBroadcastService,
    private authService: AuthService
  ) { }

  /** OIDC authorize URLs after CRM login (return_url from Oauth.php). */
  private redirectReturnUrlIfPresent(): boolean {
    const raw = this.route.snapshot.queryParamMap.get('return_url');
    if (!raw) return false;
    try {
      const target = new URL(raw, window.location.origin);
      if (target.origin !== window.location.origin) return false;
      if (!target.pathname.startsWith('/api/oauth/authorize')) return false;
      window.location.assign(target.toString());
      return true;
    } catch {
      return false;
    }
  }

  ngOnInit(): void {
    this.notifyIdleSignOut();

    try {
      this.msalService.instance.handleRedirectPromise().then(async res => {
        if (res != null && res.account != null) {
          this.showMsalRedirect = true;
          this.msalService.instance.setActiveAccount(res.account)
          await firstValueFrom(this.authService.checkSession());
          if (this.authService.currentUser()) {
            // No identity to copy over: HelperService reads AuthService on every access.
            if (!this.helperService.allUsers || this.helperService.allUsers.length == 0) {
              await firstValueFrom(this.helperService.get_all_users());
            }
          }
        }

        // No redirect result: try a silent reconnection when a Microsoft
        // account is still cached (backend session expired, AAD session alive).
        if (res == null && !this.authService.currentUser()) {
          await this.trySilentReconnect();
        }

        if (!this.redirectReturnUrlIfPresent()) {
          this.helperService.redirectToDashboard();
        }
      }).catch(() => {
        // A prompt=none recovery bounce may come back with an Entra error
        // (AAD session expired): the app session can still be valid, so send
        // the user to the dashboard instead of stranding them on /login.
        if (this.authService.currentUser()) {
          this.helperService.redirectToDashboard();
        }
      })

      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status !== InteractionStatus.None),
          takeUntil(this._destroy$)
        )
        .subscribe(() => this.interactionInProgress = true);

      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          takeUntil(this._destroy$)
        )
        .subscribe(() => this.interactionInProgress = false);
    } catch (error) {
      console.error(error);
    }
  }

  /** Tell the user why they were sent back here after an inactivity sign-out. */
  private notifyIdleSignOut(): void {
    if (sessionStorage.getItem(IDLE_SIGNED_OUT_STORAGE_KEY) !== '1') return;
    sessionStorage.removeItem(IDLE_SIGNED_OUT_STORAGE_KEY);
    this.helperService.openSnackBar(IDLE_SIGNED_OUT_MESSAGE, 'Close', 8000);
  }

  get f() {
    return this.form.controls;
  }

  /**
   * Rebuild the backend session without user interaction:
   * cached MSAL account -> silent Graph token -> login_microsoft is replayed
   * server-side. Falls back to ONE automatic loginRedirect per tab (Azure AD
   * SSO cookie makes it transparent); afterwards the manual button remains.
   */
  private async trySilentReconnect(): Promise<void> {
    // The user explicitly logged out: wait for a manual sign-in click.
    if (this.authService.hasExplicitLogout()) return;

    // Hard anti-loop breaker: at most ONE automatic reconnection attempt per
    // minute per tab, whatever drives us back to /login.
    const lastAttempt = Number(sessionStorage.getItem('ms_reconnect_last_attempt') || '0');
    if (Date.now() - lastAttempt < 60_000) return;
    sessionStorage.setItem('ms_reconnect_last_attempt', String(Date.now()));

    const account: AccountInfo | undefined = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];
    if (!account) return;

    this.showMsalRedirect = true;
    try {
      const tokenResponse = await this.msalService.instance.acquireTokenSilent({
        scopes: ['user.read'],
        account,
      });

      await firstValueFrom(this.authService.login_microsoft({
        username: account.username,
        access_token: tokenResponse.accessToken,
      }));

      const user = this.authService.currentUser();
      if (user) {
        sessionStorage.removeItem('ms_auto_login_attempted');
        if (!this.helperService.allUsers || this.helperService.allUsers.length == 0) {
          await firstValueFrom(this.helperService.get_all_users());
        }
      }
    } catch {
      // Silent flow failed (refresh token expired after 24h for SPAs).
      // One automatic redirect per tab: AAD SSO completes it without typing.
      this.showMsalRedirect = false;
      if (!sessionStorage.getItem('ms_auto_login_attempted')) {
        sessionStorage.setItem('ms_auto_login_attempted', '1');
        this.msalService.loginRedirect({
          scopes: ['user.read'],
          loginHint: account.username,
        });
      }
    }
  }

  login() {
    // Manual sign-in: lift the explicit-logout latch and the auto-retry guards.
    this.authService.clearExplicitLogout();
    sessionStorage.removeItem('ms_auto_login_attempted');
    this.msalService.loginRedirect();
    /*if (this.msalGuardConfig.authRequest && this.interactionInProgress) {
      this.authServiceMsal.loginRedirect({
        ...this.msalGuardConfig.authRequest,
        redirectUri: environment.redirectUri,
      } as RedirectRequest);
    } else {
      this.authServiceMsal.loginRedirect({
        scopes: ['user.read'],
        redirectUri: environment.redirectUri,
      });
    }*/
  }

  /*ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }*/

}
